import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { createClient as createClientRecord } from '../data/clients';
import { clientSearchQueryKey, useClientSearchQuery } from '../domain/clients/useClients';
import type { ClientSummary } from '../domain/clients';
import { getSupabaseClient } from '../data/supabase';
import { runSupabaseQuery, safeErrorMessage } from '../utils/appErrors';

export function useClientSearch(supabase?: SupabaseClient) {
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const queryClient = useQueryClient();
  const client = supabase ?? getSupabaseClient();

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const searchResult = useClientSearchQuery({
    term: debouncedQuery,
    supabase: client,
    enabled: debouncedQuery.trim().length > 0,
  });

  const createClientMutation = useMutation({
    mutationFn: (name: string) => runSupabaseQuery(
      () => createClientRecord(name, { supabase: client }),
      'Unable to create the customer.',
      'client_create',
      'mutation',
    ),
    onSuccess: (data) => {
      if (!data) return;
      queryClient.setQueryData<ClientSummary[] | undefined>(
        clientSearchQueryKey(debouncedQuery),
        (prev) => {
          if (!prev) return [data];
          if (prev.find((entry) => entry.id === data.id)) return prev;
          return [data, ...prev];
        },
      );
    },
  });

  return {
    query,
    setQuery,
    results: debouncedQuery ? searchResult.data ?? [] : [],
    loading: searchResult.isFetching || createClientMutation.isPending,
    isSuccess: searchResult.isSuccess,
    error: searchResult.error
      ? safeErrorMessage(searchResult.error, 'Unable to search customers.')
      : createClientMutation.error
        ? safeErrorMessage(createClientMutation.error, 'Unable to create the customer.')
        : undefined,
    createClient: (name: string) => createClientMutation.mutateAsync(name),
  };
}
